'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  Accordion,
  Button,
  Checkbox,
  Group,
  Modal,
  MultiSelect,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { RichBlocksManager } from '@/src/components/RichBlocks/RichBlocksManager';
import { primaryColor, secondaryColor } from '@/src/styles/colors';
import type { RichBlock } from '@/src/types/RichBlock';
import { Dropzone } from '../EditorText/Dropzone';
import { Editor } from '../EditorText/Editor';

export const EditionModale = ({
  content,
  id,
  title: initialTitle,
  subtitle: initialSubtitle,
  description: initialDescription,
  mainImage: initialMainImage,
  richBlocks: initialRichBlocks = [],
  references: initialReferences = [],
  author: initialAuthor,
  category: initialCategory,
  tags: initialTags = [],
  seoTitle: initialSeoTitle,
  seoDescription: initialSeoDescription,
  seoKeywords: initialSeoKeywords = [],
  showOnSite: initialShowOnSite = true,
  lang = 'fr',
}: {
  content: string;
  id: string;
  title: string;
  subtitle?: string;
  description: string;
  mainImage: string;
  richBlocks?: RichBlock[];
  references?: Array<{ number: number; citation: string; url?: string }>;
  author?: string;
  category?: string;
  tags?: string[];
  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string[];
  showOnSite?: boolean;
  lang?: string;
}) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [editorContent, setEditorContent] = useState<string>(content);
  const [title, setTitle] = useState(initialTitle);
  const [subtitle, setSubtitle] = useState(initialSubtitle ?? '');
  const [description, setDescription] = useState(initialDescription);
  const [mainImage, setMainImage] = useState<string | null>(initialMainImage);
  const [mainImageError, _setMainImageError] = useState<string | undefined>(undefined);
  const [disconnected, _setDisconnected] = useState(false);
  const [_validated, _setValidated] = useState(false);
  const [richBlocks, setRichBlocks] = useState<RichBlock[]>(initialRichBlocks);
  const [references, setReferences] =
    useState<Array<{ number: number; citation: string; url?: string }>>(initialReferences);
  const [author, setAuthor] = useState(initialAuthor ?? '');
  const [category, setCategory] = useState(initialCategory ?? 'article');
  const [tags, setTags] = useState<string[]>(initialTags);
  const [seoTitle, setSeoTitle] = useState(initialSeoTitle ?? '');
  const [seoDescription, setSeoDescription] = useState(initialSeoDescription ?? '');
  const [seoKeywords, setSeoKeywords] = useState<string[]>(initialSeoKeywords);
  const [showOnSite, setShowOnSite] = useState(initialShowOnSite);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const router = useRouter();

  const handleOpenClick = () => {
    open();
  };

  const saveArticle = async () => {
    setError(null);
    setSaving(true);

    const body: Record<string, any> = {
      content: editorContent,
      title,
      subtitle,
      description,
      mainImage,
      richBlocks,
      references,
      author,
      category,
      tags,
      seoTitle,
      seoDescription,
      seoKeywords,
      showOnSite,
      lang,
    };

    const response = await fetch(`/api/articles/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    setSaving(false);

    if (!response.ok) {
      const json = await response.json().catch(() => null);
      setError(json?.message ?? 'Erreur lors de la sauvegarde.');
      console.error('❌ Failed to save article.');
    } else {
      console.log('✅ Article updated successfully.');
      close();
      router.refresh();
    }
  };

  const deleteArticle = async () => {
    const response = await fetch(`/api/articles/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      console.error('❌ Failed to delete article.');
      response.status === 401 && router.push('/login');
    } else {
      console.log('🗑️ Article deleted successfully.');
      router.push('/articles');
    }
  };

  const addReference = () => {
    setReferences([...references, { number: references.length + 1, citation: '', url: '' }]);
  };

  const updateReference = (
    index: number,
    patch: Partial<{ number: number; citation: string; url?: string }>
  ) => {
    setReferences(references.map((ref, i) => (i === index ? { ...ref, ...patch } : ref)));
  };

  const removeReference = (index: number) => {
    setReferences(references.filter((_, i) => i !== index));
  };

  return (
    <>
      <Group>
        <Button onClick={() => handleOpenClick()} color={primaryColor}>
          Modifier l'article
        </Button>
        <Button onClick={() => deleteArticle()} color={secondaryColor}>
          Supprimer l'article
        </Button>
      </Group>

      <>
        <Modal
          opened={disconnected}
          onClose={() => {
            router.push('/login');
          }}
          title="Session Expired"
          centered
        >
          <p>Your session has expired. Please log in again.</p>
          <Button onClick={() => router.push('/login')}>Go to Login</Button>
        </Modal>
      </>

      <Modal opened={opened} onClose={close} title="Modifier l'article" size="xl">
        <TextInput
          label="Titre"
          placeholder="Titre de l'article"
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          withAsterisk
          mb="md"
        />
        <TextInput
          label="Sous-titre"
          placeholder="Sous-titre"
          value={subtitle}
          onChange={(e) => setSubtitle(e.currentTarget.value)}
          mb="md"
        />
        <Textarea
          label="Description"
          placeholder="Courte description"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          autosize
          minRows={2}
          mb="md"
        />

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 5 }}>
            Image principale
          </label>
          <Dropzone setImageUrl={setMainImage} imageUrl={mainImage} error={mainImageError} />
        </div>

        <Editor content={editorContent} setEditorContent={setEditorContent} />

        <RichBlocksManager blocks={richBlocks} onChange={setRichBlocks} title="Blocs enrichis" />

        <Accordion variant="contained" mb="md">
          <Accordion.Item value="references">
            <Accordion.Control>Références ({references.length})</Accordion.Control>
            <Accordion.Panel>
              {references.length === 0 && (
                <Text c="dimmed" size="sm" mb="sm">
                  Aucune référence.
                </Text>
              )}
              {references.map((ref, index) => (
                <Group key={index} mb="sm" align="flex-start">
                  <TextInput
                    value={ref.citation}
                    onChange={(e) => updateReference(index, { citation: e.target.value })}
                    placeholder="Citation"
                    style={{ flex: 1 }}
                  />
                  <TextInput
                    value={ref.url ?? ''}
                    onChange={(e) => updateReference(index, { url: e.target.value })}
                    placeholder="URL"
                    style={{ flex: 1 }}
                  />
                  <Button
                    variant="subtle"
                    color="red"
                    size="xs"
                    onClick={() => removeReference(index)}
                  >
                    Supprimer
                  </Button>
                </Group>
              ))}
              <Button variant="light" size="sm" onClick={addReference}>
                Ajouter une référence
              </Button>
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item value="seo">
            <Accordion.Control>SEO & paramètres</Accordion.Control>
            <Accordion.Panel>
              <TextInput
                label="Auteur"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                mb="md"
              />
              <TextInput
                label="Catégorie"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                mb="md"
              />
              <MultiSelect
                label="Tags"
                data={tags}
                value={tags}
                onChange={setTags}
                placeholder="Tags"
                searchable
                mb="md"
              />
              <TextInput
                label="Titre SEO"
                value={seoTitle}
                onChange={(e) => setSeoTitle(e.target.value)}
                mb="md"
              />
              <Textarea
                label="Description SEO"
                value={seoDescription}
                onChange={(e) => setSeoDescription(e.target.value)}
                minRows={2}
                mb="md"
              />
              <MultiSelect
                label="Mots-clés SEO"
                data={seoKeywords}
                value={seoKeywords}
                onChange={setSeoKeywords}
                placeholder="Mots-clés SEO"
                searchable
                mb="md"
              />
              <Checkbox
                label="Visible sur le site"
                checked={showOnSite}
                onChange={(e) => setShowOnSite(e.currentTarget.checked)}
              />
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>

        {error && (
          <Text c="red" size="sm" mb="md">
            {error}
          </Text>
        )}

        <Button onClick={() => saveArticle()} color="green" fullWidth mt="md" loading={saving}>
          Sauvegarder les modifications
        </Button>
      </Modal>
    </>
  );
};
